import EventOriginLink, { formatEventOrigin } from '../components/EventOriginLink';
import React from 'react';
import StatusPill from '../../../shared/components/patterns/StatusPill';
import { CALENDAR_EVENT_TYPE_OPTIONS, EVENT_TYPE_META } from '../utils/calendar.constants';
import {
  getPriorityLabel,
  isEventCompleted,
  normalizeEventType,
} from '../utils/calendar.helpers';

export const calendarFilterConfig = {
  label: 'Filtri calendario',
  search: {
    key: 'query',
    placeholder: 'Cerca eventi...',
    label: 'Cerca eventi',
  },
  groups: [
    {
      key: 'type',
      label: 'Tipo',
      selection: 'multi',
      options: CALENDAR_EVENT_TYPE_OPTIONS.map((item) => ({
        value: item.value,
        label: item.label,
      })),
    },
    {
      key: 'priority',
      label: 'Priorità',
      selection: 'multi',
      options: [
        { value: 'alta', label: 'Alta' },
        { value: 'media', label: 'Media' },
        { value: 'bassa', label: 'Bassa' },
      ],
    },
    {
      key: 'status',
      label: 'Stato',
      selection: 'single',
      emptyValue: '',
      options: [
        { value: 'pending', label: 'Da completare' },
        { value: 'completed', label: 'Completato' },
      ],
    },
    {
      key: 'range',
      label: 'Periodo',
      selection: 'single',
      emptyValue: '',
      options: [
        { value: 'today', label: 'Oggi' },
        { value: 'next7days', label: 'Prossimi 7 giorni' },
        { value: 'next15', label: 'Prossimi 15 giorni' },
        { value: 'month', label: 'Questo mese' },
      ],
    },
  ],
};

export const initialCalendarFilters = {
  query: '',
  type: [],
  priority: [],
  status: '',
  range: '',
  staffId: '',
  clientId: '',
  supplierId: '',
};

export const calendarMetricConfig = [
  {
    id: 'meeting',
    label: 'Meeting',
    iconKey: 'overview.calendarMeeting',
  },
  {
    id: 'meetings',
    label: 'Riunioni',
    iconKey: 'overview.calendarMeetings',
  },
  {
    id: 'completed',
    label: 'Completati',
    iconKey: 'overview.calendarCompleted',
  },
  {
    id: 'month',
    label: 'Questo Mese',
    iconKey: 'overview.calendarMonth',
  },
];

export const calendarPriorityToneMap = {
  alta: 'danger',
  media: 'approved',
  bassa: 'neutral',
};

export const CALENDAR_EVENT_TYPE_TONES = {
  meeting: 'blue',
  riunione: 'purple',
  internal: 'purple',
  payment: 'red',
  pagamento: 'red',
  income: 'green',
  incasso: 'green',
  deadline: 'orange',
  scadenza: 'orange',
  training: 'teal',
  reminder: 'teal',
  promemoria: 'teal',
  task: 'purple',
  default: 'gray',
};

export function buildCalendarMetrics(events = []) {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const values = {
    meeting: events.filter((event) => normalizeEventType(event.type) === 'meeting').length,
    meetings: events.filter((event) => ['meeting', 'internal'].includes(normalizeEventType(event.type)))
      .length,
    completed: events.filter(isEventCompleted).length,
    month: events.filter((event) => {
      if (!event.date) return false;
      const date = new Date(`${event.date}T00:00:00`);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    }).length,
  };

  return calendarMetricConfig.map((metric) => ({
    ...metric,
    value: values[metric.id] ?? 0,
    tone: metric.id === 'completed' ? 'success' : 'neutral',
  }));
}

export function getEventTypeLabel(type) {
  return EVENT_TYPE_META[type]?.label || 'Altro';
}

export function getEventTypeTone(type) {
  return `event-${getCalendarGridEventTone(type)}`;
}

export function getPriorityTone(priority) {
  return calendarPriorityToneMap[priority] || 'neutral';
}

export function getCalendarGridEventTone(type) {
  return CALENDAR_EVENT_TYPE_TONES[normalizeEventType(type)] || CALENDAR_EVENT_TYPE_TONES.default;
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value);
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
}

function getFinancialDetailLabel(event, key) {
  return event?.financialDetails?.[key] || '—';
}

export function buildCalendarColumns({ onOpenEvent } = {}) {
  return [
    {
      id: 'title',
      render: (event) => <span className="calendar-table__name">{event.title || 'Evento senza titolo'}</span>,
    },
    {
      id: 'origin',
      order: 1,
      accessor: formatEventOrigin,
      sortable: true,
      header: 'Origine',
      render: (event) => <EventOriginLink event={event} onOpenEvent={onOpenEvent} />,
    },
    {
      id: 'linked',
      order: 4,
      header: 'Collegamento',
      render: (event) => event.clientName || event.supplierName || event.linkedEntity?.label || '—',
    },
    {
      id: 'paymentDetails',
      order: 5,
      header: 'Pagamento',
      render: (event) =>
        event.financialDetails ? (
          <div className="calendar-table__payment">
            <small>{getFinancialDetailLabel(event, 'counterpartyLabel')}</small>
            <span>{getFinancialDetailLabel(event, 'counterpartyName')}</span>
            <strong>{formatMoney(event.financialDetails.amount)}</strong>
            <small>{getFinancialDetailLabel(event, 'reference')}</small>
          </div>
        ) : (
          event.amount != null ? <strong>{formatMoney(event.amount)}</strong> : '—'
        ),
    },
    {
      id: 'type',
      render: (event) => (
        <StatusPill label={getEventTypeLabel(event.type)} tone={getEventTypeTone(event.type)} />
      ),
    },
    {
      id: 'priority',
      render: (event) => (
        <StatusPill label={getPriorityLabel(event.priority)} tone={getPriorityTone(event.priority)} />
      ),
    },
  ];
}

export function buildCalendarRowActions({
  onDetail,
  onEdit,
  onComment,
  onDelete,
  onSourceAction,
  canDelete = false,
}) {
  return (event) => [
    {
      id: 'preview',
      iconKey: 'actions.preview',
      label: 'Apri dettaglio evento',
      onClick: () => onDetail(event),
    },
    ...(!event.isReadOnly
      ? [
          {
            id: 'edit',
            iconKey: 'actions.edit',
            label: 'Modifica evento',
            onClick: () => onEdit(event),
          },
          {
            id: 'comment',
            iconKey: 'actions.comment',
            label: 'Commenta evento',
            onClick: () => onComment(event),
          },
          ...(canDelete
            ? [
                {
                  id: 'delete',
                  iconKey: 'actions.delete',
                  label: 'Elimina evento',
                  onClick: () => onDelete(event),
                  tone: 'danger',
                },
              ]
            : []),
        ]
      : [
          {
            id: 'edit-source',
            iconKey: 'actions.edit',
            label: 'Modifica fonte',
            onClick: () => onSourceAction(event, 'edit'),
          },
          {
            id: 'comment-source',
            iconKey: 'actions.comment',
            label: 'Commenta fonte',
            onClick: () => onSourceAction(event, 'comments'),
          },
          ...(canDelete
            ? [
                {
                  id: 'delete-source',
                  iconKey: 'actions.delete',
                  label: 'Elimina fonte',
                  onClick: () => onSourceAction(event, 'delete'),
                  tone: 'danger',
                },
              ]
            : []),
        ]),
  ];
}

export function resetCalendarFilters() {
  return { ...initialCalendarFilters };
}
